/** @jsxImportSource @emotion/react */
import { presentBathCount, presentUser, propertyIdPresenter } from '@kaiizen/kai-presenters';
import type { ApiPropertyProject } from '@kaiizen/kai-pro-api';
import {
  usePropertyHeroForPropertyProject,
  usePropertyHeroForScan,
  useUsers,
  useUsersForScan,
} from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useRef, useState } from 'react';

import { useProjectTypes } from '@/Client/State';
import { MoreVerticalIcon } from '@kaiizen/kai-shiki/react';
import { formatDashboardDate } from './dashboardHelpers';
import {
  getTurnBasedDashboardAddress,
  getTurnBasedDashboardArtifactPresentation,
  getTurnBasedDashboardDueDate,
  getTurnBasedDashboardPersonName,
  isTurnBasedDashboardProjectPastDue,
} from './turnBasedDashboardLib';
import { TURN_BASED_DASHBOARD_PROJECT_ROW_STYLES as styles } from './turnBasedDashboardProjectRowStyles';

/** Renders the larger, turn-aware project card used only by the flagged dashboard. */
export const TurnBasedDashboardProjectRow = ({
  project,
  marketName,
  propertyIdLabel,
  userId,
  onArchive,
  onUnarchive,
  onReactivate,
  actionInFlight = false,
  onClick,
}: {
  project: ApiPropertyProject;
  marketName?: string;
  propertyIdLabel: string;
  userId?: string;
  onArchive?: (project: ApiPropertyProject) => void;
  onUnarchive?: (project: ApiPropertyProject) => void;
  onReactivate?: (project: ApiPropertyProject) => void;
  actionInFlight?: boolean;
  onClick: () => void;
}) => {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const scanHeroUri = usePropertyHeroForScan(project.latestScan, project);
  const projectHeroUri = usePropertyHeroForPropertyProject(
    project.latestScan ? undefined : project,
  );
  const { users } = useUsers();
  const { usersForScan } = useUsersForScan(project.firstScan?.id);
  const { getProjectTypeLabel } = useProjectTypes();
  const artifact = getTurnBasedDashboardArtifactPresentation(project);
  const property = project.orgProperty;
  const facts = property?.customerMetadata;
  const { streetAddress, locality } = getTurnBasedDashboardAddress(property?.address);
  const dueDate = getTurnBasedDashboardDueDate(project);
  const projectTypeLabel = project.customerMetadata?.projectType
    ? getProjectTypeLabel(project.customerMetadata.projectType)?.toUpperCase()
    : undefined;
  const bathCount = presentBathCount(
    facts?.fullBathCount,
    facts?.halfBathCount,
    facts?.bathroomCount,
  );
  const assigneeCount = new Set([
    ...(project.managerIds ?? []),
    ...(usersForScan ?? []).map(user => user.id),
  ]).size;
  const assigneeLabel = assigneeCount > 0
    ? `${assigneeCount} ${assigneeCount === 1 ? 'assignee' : 'assignees'}`
    : '--';
  const responsibleUser = users?.find(user => user.id === project.responsibleUserId);
  const responsibleUserName = getTurnBasedDashboardPersonName(
    responsibleUser ? presentUser(responsibleUser) : undefined,
  );
  const isYourTurn = !!userId && project.responsibleUserId === userId;
  const amount = typeof project.customerMetadata?.budget === 'number'
    ? `$${
      project.customerMetadata.budget.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })
    }`
    : '--';
  const isPastDue = isTurnBasedDashboardProjectPastDue(project);
  const isArchived = !!project.archivedAt;
  const isCompleted = project.status === 'completed';
  const heroUri = scanHeroUri ?? projectHeroUri;
  const shouldShowActionButton = isCardHovered || isActionMenuOpen;

  useEffect(() => {
    if (!isActionMenuOpen) return undefined;

    const closeActionMenu = (event: MouseEvent) => {
      if (!actionMenuRef.current?.contains(event.target as Node)) {
        setIsActionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeActionMenu);
    return () => document.removeEventListener('mousedown', closeActionMenu);
  }, [isActionMenuOpen]);

  return (
    <div
      css={styles.projectCard}
      onClick={onClick}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
    >
      <div css={styles.photoFrame}>
        {heroUri
          ? <img css={styles.photo} src={heroUri} alt={property?.address ?? ''} />
          : <div css={styles.photoPlaceholder}>No Photo</div>}
      </div>
      <div css={styles.projectDetails}>
        <div css={styles.projectTop}>
          <div>
            <div css={styles.addressLine}>
              <span css={styles.address}>{streetAddress}</span>
              {!!project.name && <span css={styles.projectName}>{`· ${project.name}`}</span>}
            </div>
            <div css={styles.propertyLine}>
              {locality && <span>{locality}</span>}
              <span css={styles.projectFacts}>
                {`${facts?.bedroomCount ?? '--'} bed · ${bathCount} bath · ${facts?.squareFeet ? `${facts.squareFeet.toLocaleString()} sqft` : '--'}`}
              </span>
            </div>
          </div>
          <div css={styles.projectActions}>
            <div css={[styles.amount, amount === '--' && styles.amountUnset]}>
              {amount}
              <span css={styles.amountLabel}>Amount</span>
            </div>
            <div
              css={styles.actionMenu}
              ref={actionMenuRef}
              onClick={event => event.stopPropagation()}
            >
              <button
                type='button'
                css={[styles.actionButton, shouldShowActionButton && styles.actionButtonVisible]}
                aria-label='Open project actions'
                onClick={() => setIsActionMenuOpen(open => !open)}
              >
                <MoreVerticalIcon size={15} aria-hidden />
              </button>
              {isActionMenuOpen && (
                <div css={styles.actionPanel}>
                  {isArchived
                    ? (
                      <button
                        type='button'
                        css={styles.actionMenuItem}
                        disabled={actionInFlight}
                        onClick={() => {
                          setIsActionMenuOpen(false);
                          onUnarchive?.(project);
                        }}
                      >
                        Unarchive Project
                      </button>
                    )
                    : isCompleted
                    ? (
                      <>
                        <button
                          type='button'
                          css={styles.actionMenuItem}
                          disabled={actionInFlight}
                          onClick={() => {
                            setIsActionMenuOpen(false);
                            onReactivate?.(project);
                          }}
                        >
                          Reactivate Project
                        </button>
                        <button
                          type='button'
                          css={styles.actionMenuItem}
                          disabled={actionInFlight}
                          onClick={() => {
                            setIsActionMenuOpen(false);
                            onArchive?.(project);
                          }}
                        >
                          Archive Project
                        </button>
                      </>
                    )
                    : (
                      <button
                        type='button'
                        css={styles.actionMenuItem}
                        disabled={actionInFlight}
                        onClick={() => {
                          setIsActionMenuOpen(false);
                          onArchive?.(project);
                        }}
                      >
                        Archive Project
                      </button>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div css={styles.projectMetadata}>
          <div>
            <div css={styles.metadataLabel}>Job</div>
            <div css={styles.metadataTagRow}>
              {projectTypeLabel && <span css={[styles.tag, styles.jobTag]}>{projectTypeLabel}</span>}
              <span css={[styles.tag, styles.artifactTag]}>{artifact.label}</span>
            </div>
          </div>
          <div>
            <div css={styles.metadataLabel}>Market</div>
            <div css={styles.metadataValue}>{marketName ?? '--'}</div>
          </div>
          <div>
            <div css={styles.metadataLabel}>{propertyIdLabel}</div>
            <div css={styles.metadataValue}>{propertyIdPresenter(property)}</div>
          </div>
          <div>
            <div css={styles.metadataLabel}>Due Date</div>
            <div css={[styles.metadataValue, isPastDue && styles.metadataAlert]}>
              {formatDashboardDate(dueDate)}
            </div>
          </div>
          <div>
            <div css={styles.metadataLabel}>Last Updated</div>
            <div css={styles.metadataValue}>{formatDashboardDate(project.updatedAt)}</div>
          </div>
          <div>
            <div css={styles.metadataLabel}>Waiting on</div>
            <div css={styles.metadataValue}>
              <span css={isYourTurn ? styles.waitingOnYou : undefined}>
                {isYourTurn ? 'You' : responsibleUserName ?? '--'}
              </span>
            </div>
          </div>
          <div>
            <div css={styles.metadataLabel}>Assignees</div>
            <div css={styles.metadataValue}>{assigneeLabel}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
